import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import api from '@/src/services/api';
import useGallery from '../hooks/useGallery';
import GlobalStyle from '@/src/Share/components/GalleryTheme';
import PaymentForm from './PaymentForm';

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const unavailable = 'This checkout is temporarily unavailable. Please ask the booth operator for help before paying again.';
const payable = (payment: any) => !payment.paidAt && !payment.invalidatedAt &&
    ['awaiting_payment', 'requires_payment_method', 'requires_confirmation', 'requires_action'].includes(payment.status);
function validatePurchase(payment: any, activationId: number, token: string) {
    if (!payment || payment.activationId !== activationId || payment.redemptionCode !== token ||
        !['awaiting_payment', 'requires_payment_method', 'requires_confirmation', 'requires_action', 'processing',
            'requires_capture', 'succeeded', 'canceled', 'refunded', 'partially_refunded'].includes(payment.status) ||
        ![payment.subtotalCents, payment.taxCents, payment.totalCents].every(value => Number.isSafeInteger(value) && value >= 0) ||
        payment.totalCents !== payment.subtotalCents + payment.taxCents ||
        !Number.isFinite(payment.taxRatePercent) || payment.taxRatePercent < 0) {
        throw new Error(unavailable);
    }
    return payment;
}
export default function PaymentLanding() {
    const { token, slug } = useParams();
    const { getGallery } = useGallery({ slug: slug || '', galleryId: 0 });
    const [gallery, setGallery] = useState<any>(null);
    const [purchase, setPurchase] = useState<any>(null);
    const [secret, setSecret] = useState<string | null>(null);
    const [stripe, setStripe] = useState<Promise<Stripe | null> | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [attempt, setAttempt] = useState(0);
    const generation = useRef(0);
    useEffect(() => {
        const current = ++generation.current;
        let stopped = false;
        setLoading(true); setError(''); setSecret(null); setStripe(null); setPurchase(null); setGallery(null);
        (async () => {
            try {
                const galleryInfo = await getGallery();
                const activationId = galleryInfo?.activationId;
                if (!activationId || !token) throw new Error('Purchase not found');
                const base = `/payments/${activationId}/sessionPayments`;
                let payment = validatePurchase((await api.get(`${base}/${token}`)).data, activationId, token);
                if (stopped || generation.current !== current) return;
                if (payable(payment)) {
                    await api.put(`${base}/claim`, { activationId, redemptionCode: token });
                    if (stopped || generation.current !== current) return;
                    payment = validatePurchase((await api.post(`${base}/createPaymentIntent`, { activationId, redemptionCode: token })).data, activationId, token);
                }
                if (stopped || generation.current !== current) return;
                if (payable(payment)) {
                    if (typeof payment.clientSecret !== 'string' || !payment.clientSecret ||
                        typeof payment.stripePublishableKey !== 'string' || !payment.stripePublishableKey) throw new Error(unavailable);
                    setStripe(loadStripe(payment.stripePublishableKey)); setSecret(payment.clientSecret);
                }
                setGallery(galleryInfo); setPurchase(payment);
            } catch (e: any) {
                if (!stopped) setError(e.message === unavailable ? unavailable : 'Could not load this purchase. Retry to check its status.');
            } finally { if (!stopped) setLoading(false); }
        })();
        return () => { stopped = true; };
        // getGallery is recreated by its hook. The route/retry owns this request lifecycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, slug, attempt]);
    useEffect(() => {
        if (!gallery?.activationId || !token) return;
        let stopped = false;
        let timer: ReturnType<typeof setTimeout>;
        const poll = async () => {
            try {
                const next = validatePurchase((await api.get(`/payments/${gallery.activationId}/sessionPayments/${token}`)).data, gallery.activationId, token);
                if (!stopped) { setPurchase(next); setError(''); }
            } catch (e: any) {
                if (!stopped) {
                    setError(e.message === unavailable ? unavailable : 'Reconnecting to check payment status. Please do not pay again.');
                    if (e.message === unavailable) {
                        setSecret(null); setStripe(null); setPurchase(null);
                        stopped = true; // Require a full status/checkout reload before showing a form again.
                    }
                }
            }
            finally { if (!stopped) timer = setTimeout(poll, 3000); }
        };
        timer = setTimeout(poll, 3000);
        return () => { stopped = true; clearTimeout(timer); };
    }, [gallery?.activationId, token]);
    const reconcile = async () => {
        const result = await api.put(`/payments/${gallery.activationId}/sessionPayments/pay`, {
            activationId: gallery.activationId, redemptionCode: token,
        });
        setPurchase(validatePurchase(result.data, gallery.activationId, token || ''));
    };
    const refunded = ['refunded', 'partially_refunded'].includes(purchase?.status);
    const paid = !!purchase?.paidAt && !refunded;
    const cancelled = !paid && (!!purchase?.invalidatedAt || purchase?.status === 'canceled');
    const processing = ['processing', 'requires_capture'].includes(purchase?.status);
    const acknowledged = Object.keys(purchase?.fulfillment || {}).some(k => k.endsWith(':acknowledged'));
    return <div>
        <GlobalStyle foregroundColor={gallery?.foregroundColor} backgroundColor={gallery?.backgroundColor}
            headerBackgroundColor={gallery?.headerBackgroundColor} headerForegroundColor={gallery?.headerForegroundColor}
            backgroundImage={gallery?.backgroundImage} />
        <header className="Payment-header px-6 py-6 sm:py-8">
            <div className="mx-auto flex max-w-[500px] justify-center text-center">
                {gallery?.logo ? <>
                    <img src={gallery.logo} alt={gallery.name ? `${gallery.name} logo` : 'Gallery logo'}
                        className="h-20 sm:h-24 w-full max-w-[280px] object-contain" />
                    <h1 className="sr-only">Payment{gallery.name ? ` for ${gallery.name}` : ''}</h1>
                </> : <h1 className="text-2xl font-bold break-words min-w-0">{gallery?.name || 'Payment'}</h1>}
            </div>
        </header>
        <main className="mx-auto max-w-[500px] p-6 mt-6">
            {loading ? <p role="status">Checking your purchase…</p> : <>
                {refunded ? <p>This payment has been {purchase.status === 'refunded' ? 'refunded' : 'partially refunded'}. Please see the booth operator.</p>
                    : paid ? <div role="status"><h2 className="text-2xl font-bold">Payment received</h2>
                        <p>{acknowledged ? 'The booth has received your payment. Follow the instructions on its screen.' : 'Waiting for the booth to receive your payment. Please do not pay again.'}</p>
                        {purchase.recoveryRequired && <p>Please ask the booth operator to recover your purchase.</p>}</div>
                    : cancelled ? <p>This checkout has been cancelled or expired. Return to the booth to choose a package.</p>
                    : processing ? <p role="status">Your payment is processing. Please do not pay again.</p>
                    : purchase && <>
                        <dl className="space-y-2 mb-6">
                            <div className="flex justify-between"><dt>Subtotal</dt><dd>{money(purchase.subtotalCents)}</dd></div>
                            <div className="flex justify-between"><dt>Sales tax ({purchase.taxRatePercent || 0}%)</dt><dd>{money(purchase.taxCents)}</dd></div>
                            <div className="flex justify-between font-bold"><dt>Total</dt><dd>{money(purchase.totalCents)}</dd></div>
                        </dl>
                        {stripe && secret && <Elements key={secret} stripe={stripe} options={{ clientSecret: secret }}>
                            <PaymentForm clientSecret={secret} totalCents={purchase.totalCents} setError={setError} onSuccess={reconcile} onPending={reconcile} />
                        </Elements>}
                    </>}
                {error && <p role="alert" className="mt-4">{error}</p>}
                {!purchase && <button type="button" className="mt-4 underline" onClick={() => setAttempt(n => n + 1)}>Retry status check</button>}
            </>}
        </main>
    </div>;
}
